import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChildren,
  QueryList,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import {
  FormReturnType,
  NaturalFormConfig,
  NaturalFormFlavour,
  NaturalFormStyle,
} from "@cotecna/dynamic-view-engine";
import {
  ITEM_CREATED_SAME_NAME_ERROR_TITLE,
  ITEM_CREATED_SAME_NAME_ERROR_MESSAGE,
  WARNING_ICON,
  UNDERSTOOD_ACTION,
  INVALID_ITEM_QUANTITY_TITLE,
  INVALID_ITEM_QUANTITY_LARGE_MESSAGE,
} from "src/app/inspection/constants/constants";
import { LoadingComponent } from "src/app/inspection/dialogs/loading/loading.component";
import { Item } from "src/app/inspection/model/item";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";
import { ProductsListConfig } from "src/app/inspection/permissions-setter/permissions-interface";
import { EventsService } from "src/app/inspection/services/inspection-events-service/events.service";
import { InspectionFormService } from "src/app/inspection/services/inspection-form-service/inspection-form.service";
import { ScreenInfo } from "src/app/settings/models/screen-info";
import { ItemsService } from "../../services/items.service";
import {
  NEW_PRODUCT_CONTROLS,
  NEW_PRODUCT_FORM,
} from "../../static-forms/new-product-form";
import { fadeAnimation } from "./item-list-animation";
import { DynamicFormService } from "src/app/shared/services/dynamic-form/dynamic-form.service";
import {
  DynamicFormDialogComponent,
  DynamicFormDialogConfig,
} from "src/app/form-dialog/components/natural-form-dialog/dynamic-form-dialog.component";

@Component({
  selector: "im-item-list",
  templateUrl: "./item-list.component.html",
  styleUrls: ["./item-list.component.scss"],
  animations: [fadeAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ItemListComponent implements OnInit {
  @ViewChildren(MatMenuTrigger)
  trigger: QueryList<MatMenuTrigger>;

  @Input()
  items: Item[] = [];

  @Input()
  config: ProductsListConfig = {
    allowOpenDetails: false,
    allowOpenConfig: false,
    allowDelete: false,
  };

  @Input()
  inspectionId: string = null;

  @Input()
  selectedItem: Item = null;

  @Output()
  editedItem: EventEmitter<Item> = new EventEmitter<Item>();

  @Output()
  deletedItem: EventEmitter<Item> = new EventEmitter<Item>();

  @Output()
  selectedItemChanged: EventEmitter<Item> = new EventEmitter<Item>();

  @Output()
  editAction: EventEmitter<void> = new EventEmitter<void>();
  private readonly MAX_NUMBER_OF_ITEM_QUANTITY = 2147483647;
  private formScreens: ScreenInfo[] = [];

  constructor(
    private dialog: MatDialog,
    private inspectionFormService: InspectionFormService,
    private itemsService: ItemsService,
    private eventService: EventsService,
    private userRolePermissionsService: UserRolePermissionsService,
    private dynamicFormService: DynamicFormService,
  ) {}

  async ngOnInit() {
    if (this.creationMode()) {
      await this.setItemFormsOptions();
    }

    this.config.allowDelete =
      !this.userRolePermissionsService.isInspector() &&
      !this.userRolePermissionsService.isServiceProviderInspector();
  }

  async openDetails(item: Item, event: Event) {
    event.stopPropagation();
    this.selectedItem = item;
    this.selectedItemChanged.next(this.selectedItem);
  }

  async moreOptionsClick(event: Event) {
    event.stopPropagation();
  }

  getSelectedItemClass(item: Item): string {
    return this.selectedItem?.id === item?.id &&
      this.selectedItem?.name === item?.name
      ? "selected-item"
      : "";
  }

  getQuantityAndUnitFormatted(item: Item): string {
    return item?.quantity && item?.unit
      ? `${item.quantity} ${this.getItemUnit(item)}`
      : "-";
  }

  creationMode(): boolean {
    return this.inspectionId == null;
  }

  async editItem(item: Item, index: number, event: Event) {
    this.editAction.next();
    event.stopPropagation();

    this.trigger.toArray()[index].closeMenu();

    const dialogResult = await this.openEditItemDialog(item);

    if (dialogResult?.data) {
      var newItemName = dialogResult?.data[0].fields[0].value;
      if (this.hasOtherItemWithSameName(newItemName.trim(), item)) {
        this.openConfirmDialog(
          ITEM_CREATED_SAME_NAME_ERROR_TITLE,
          ITEM_CREATED_SAME_NAME_ERROR_MESSAGE,
          WARNING_ICON,
          UNDERSTOOD_ACTION,
        );
        return;
      }

      const newItemValue = dialogResult?.data[0].fields[1].value;
      if (this.dynamicFormService.hasInvalidQuantity(newItemValue)) {
        this.openConfirmDialog(
          INVALID_ITEM_QUANTITY_TITLE,
          INVALID_ITEM_QUANTITY_LARGE_MESSAGE,
          WARNING_ICON,
          UNDERSTOOD_ACTION,
        );
        return;
      }

      await this.updateItemFromForm(item, dialogResult.data);
      if (!this.creationMode()) {
        const loadingDialog = this.openLoadingDialog("Editing item");
        try {
          await this.itemsService.editItem(this.inspectionId, item);
        } finally {
          loadingDialog?.close();
        }
      }
      this.editedItem.next(item);
    }
  }

  private hasOtherItemWithSameName(
    itemName: string,
    modifiedItem: Item,
  ): boolean {
    const otherItems = this.items.filter((item) => item.id != modifiedItem.id);
    return otherItems?.length
      ? otherItems?.some(
          (item) => item.name.toLowerCase() === itemName.toLowerCase(),
        )
      : false;
  }
  private openConfirmDialog(
    title: string,
    message: string,
    icon: string,
    action: string,
  ): void {
    this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      panelClass: "inspection-action-dialog",
      data: <ConfirmDialogData>{
        icon,
        okButton: { text: action, color: "primary" },
        cancelButton: { text: "" },
        title,
        message,
      },
    });
  }

  async deleteItem(item: Item, index: number, event: Event) {
    event.stopPropagation();
    this.trigger.toArray()[index].closeMenu();

    let showLinkedItemsMessage: boolean = false;
    if (!this.creationMode()) {
      const events = await this.eventService.getEventList(this.inspectionId);
      events.forEach((timelogEvent) => {
        if (timelogEvent.itemId == item.id) showLinkedItemsMessage = true;
      });
    }
    const displayMessage = showLinkedItemsMessage
      ? `Are you sure you want to delete "${item.name}" from the items list?\nIf the item is deleted, all the timelog events linked to the item will be deleted.`
      : `Are you sure you want to delete "${item.name}" from the products list?`;

    const dialogResult = await this.dialog
      .open(ConfirmDialogComponent, {
        data: <ConfirmDialogData>{
          icon: "delete_outline",
          okButton: {
            text: "delete",
            color: "warn",
          },
          title: "Delete Product",
          message: displayMessage,
        },
      })
      .afterClosed()
      .toPromise();

    if (dialogResult) {
      if (!this.creationMode()) {
        const loadingDialog = this.openLoadingDialog("Deleting item");
        try {
          this.items.splice(index, 1);
          await this.itemsService.removeItem(this.inspectionId, item.id);
        } finally {
          loadingDialog.close();
        }
      }
      this.deletedItem.next(item);
    }
  }

  private getItemUnit(item: Item) {
    return item?.unit ? item.unit : "UNT";
  }

  private fillItemFormValues(item: Item, form: any[]): any[] {
    form.forEach((section) => {
      section.fields.forEach((field) => {
        switch (field.id) {
          case "name":
            field.value = item.name;
            break;
          case "quantity":
            field.value = item.quantity;
            break;
          case "unit":
            field.value = item.unit;
            field.readableValue = item.unit;
            break;
          case "form":
            field.value = item.formId;
            field.readableValue = this.formScreens.find(
              (program) => program.id === item.formId,
            )?.fullName;
            break;
        }
        field.valid = field.required && !field.value ? false : true;
      });
    });
    return form;
  }

  private async updateItemFromForm(item: Item, form: any[]): Promise<void> {
    for (const section of form) {
      for (const field of section.fields) {
        switch (field.id) {
          case "name":
            item.name = field.value;
            break;
          case "quantity":
            item.quantity = field.value;
            break;
          case "unit":
            item.unit = field.value;
            break;
          case "form":
            if (field.value) {
              item.formId = field.value;
              await this.updateItemForm(item);
            }
            break;
        }
      }
    }
  }

  private async openEditItemDialog(item: Item) {
    const itemFormSections = JSON.parse(JSON.stringify(NEW_PRODUCT_FORM));
    const itemFormControls = JSON.parse(JSON.stringify(NEW_PRODUCT_CONTROLS));
    if (!this.creationMode()) {
      let formField = itemFormSections[0].fields.find(
        (field) => field.id === "form",
      );
      if (formField) {
        formField.visible = false;
        formField.valid = true;
        formField.required = false;
      }
    }

    const filledSections = this.fillItemFormValues(item, itemFormSections);
    return await this.dialog
      .open(DynamicFormDialogComponent, {
        autoFocus: false,
        disableClose: false,
        panelClass: "creation-natural-form-dialog",
        data: {
          icon: "edit_outlined",
          title: "Edit item",
          formConfig: {
            displayMode: NaturalFormStyle.material,
            flavor: NaturalFormFlavour.informative,
            notifyFieldChange: false,
            returnType: FormReturnType.sections,
          } as NaturalFormConfig,
          sections: filledSections,
          controls: itemFormControls,
          cancelButtonText: "CANCEL",
          confirmButtonText: "SAVE",
          allowConfirmWithInvalidFrom: true,
        } as DynamicFormDialogConfig,
      })
      .afterClosed()
      .toPromise();
  }

  private async setItemFormsOptions() {
    this.formScreens = await this.inspectionFormService
      .getAllFormsForUserAccount()
      .pipe()
      .toPromise();
    const forms = this.formScreens.map((x) => ({ id: x.id, name: x.fullName }));
    NEW_PRODUCT_CONTROLS.find((control) => control.id === "form").options = [
      ...forms,
    ];
  }

  private async updateItemForm(item: Item) {
    const newForm = await this.inspectionFormService.getDynamicFormById(
      item.formId,
    );
    item.form = newForm;
  }

  private openLoadingDialog(textToDisplay: string): MatDialogRef<any> {
    return this.dialog.open(LoadingComponent, {
      data: <LoadingComponent>{
        loadingText: textToDisplay,
      },
      panelClass: "dialog",
    });
  }
}
